<script lang="ts">
    import type { SymmetrySettings } from "$lib/wasm/interaction";
    import { interaction, symmetryGuideEditing, symmetrySettings } from "$lib/state";
    import Icon from "$lib/utility/Icon.svelte";
    import Popup from "$lib/utility/Popup.svelte";

    const segmentOptions = [2, 4, 8] as const;

    let isOpen = false;

    function updateSymmetry(update: Partial<SymmetrySettings>) {
        $interaction.updateSymmetrySettings(update);
    }

    function moveGuide() {
        $symmetryGuideEditing = true;
        isOpen = false;
    }

    function resetGuide() {
        $interaction.resetSymmetryCenter();
    }

    $: hasActiveSymmetry = !!$symmetrySettings && (
        $symmetrySettings.mirror_x ||
        $symmetrySettings.mirror_y ||
        $symmetrySettings.radial_enabled
    );
</script>

<!----------------------------------------------------------------------------->

<div class="symmetry-control">
    <Popup bind:isOpen>
        <button
            slot="button"
            let:open
            type="button"
            class="symmetry-button"
            class:active={hasActiveSymmetry || $symmetryGuideEditing}
            aria-label="Symmetry"
            aria-pressed={hasActiveSymmetry || $symmetryGuideEditing}
            title="Symmetry"
            disabled={!$symmetrySettings}
            on:click={open}
        >
            <Icon id={hasActiveSymmetry ? "symmetry-on-24" : "symmetry-off-24" } secondaryColor={true} parentHover={true} />
        </button>

        {#if $symmetrySettings}
            <div class="symmetry-popover" data-testid="symmetry-popover">
                <label class="control-row">
                    <span>Mirror X</span>
                    <input
                        type="checkbox"
                        checked={$symmetrySettings.mirror_x}
                        on:change={(event) => updateSymmetry({ mirror_x: (event.currentTarget as HTMLInputElement).checked })}
                    />
                </label>

                <label class="control-row">
                    <span>Mirror Y</span>
                    <input
                        type="checkbox"
                        checked={$symmetrySettings.mirror_y}
                        on:change={(event) => updateSymmetry({ mirror_y: (event.currentTarget as HTMLInputElement).checked })}
                    />
                </label>

                <label class="control-row">
                    <span>Radial symmetry</span>
                    <input
                        type="checkbox"
                        checked={$symmetrySettings.radial_enabled}
                        on:change={(event) => updateSymmetry({ radial_enabled: (event.currentTarget as HTMLInputElement).checked })}
                    />
                </label>

                <div class="control-row">
                    <span>Segments</span>
                    <div class="segments" aria-label="Radial segments">
                        {#each segmentOptions as segments}
                            <button
                                type="button"
                                class:active={$symmetrySettings.radial_segments === segments}
                                aria-pressed={$symmetrySettings.radial_segments === segments}
                                on:click={() => updateSymmetry({ radial_segments: segments })}
                            >
                                {segments}
                            </button>
                        {/each}
                    </div>
                </div>

                <div class="actions">
                    <button type="button" class:active={$symmetryGuideEditing} on:click={moveGuide}>
                        Move guide
                    </button>
                    <button type="button" on:click={resetGuide}>
                        Reset guide
                    </button>
                </div>
            </div>
        {/if}
    </Popup>
</div>

<!----------------------------------------------------------------------------->

<style>
    .symmetry-control {
        position: relative;
        height: 100%;
        display: grid;
        place-items: center;
    }

    .symmetry-button {
        all: unset;
        width: 1.75em;
        height: 1.75em;
        display: grid;
        place-content: center;
        cursor: pointer;
    }

    .symmetry-button:disabled {
        cursor: default;
        opacity: 0.45;
    }

    .symmetry-popover {
        position: absolute;
        top: calc(100% + 0.25em);
        right: 0;
        z-index: 10;
        width: 15em;
        padding: 0.5em;
        display: grid;
        gap: 0.45em;
        background: var(--panel-bg);
        border: 1px solid var(--g100);
        box-shadow: 0 0.35em 1em rgba(0, 0, 0, 0.25);
    }

    .control-row {
        display: grid;
        grid-template-columns: minmax(0, 1fr) max-content;
        align-items: center;
        gap: 0.75em;
        color: var(--t700);
        font-size: 0.875em;
    }

    .control-row input {
        margin: 0;
    }

    .segments {
        display: grid;
        grid-template-columns: repeat(3, 2em);
        gap: 0.25em;
    }

    .segments button,
    .actions button {
        all: unset;
        box-sizing: border-box;
        min-height: 1.75em;
        display: grid;
        place-content: center;
        color: var(--t500);
        background: var(--panel-bg);
        border: 1px solid var(--g100);
        cursor: pointer;
        font-size: 0.875em;
    }

    .segments button:hover,
    .segments button:focus-visible,
    .segments button.active,
    .actions button:hover,
    .actions button:focus-visible,
    .actions button.active {
        color: var(--tacc);
        border-color: var(--gacc100);
    }

    .actions {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 0.4em;
        padding-top: 0.25em;
    }
</style>
